import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetBlogCategories } from "../../hooks/blog-categories/useGetBlogCategories";
import { useDeleteBlogCategory } from "../../hooks/blog-categories/useDeleteBlogCategory";
import { useDeleteAllBlogCategories } from "../../hooks/blog-categories/useDeleteAllBlogCategories";
import { useRestoreAllBlogCategories } from "../../hooks/blog-categories/useRestoreAllBlogCategories";
import { useForceDeleteAllBlogCategories } from "../../hooks/blog-categories/useForceDeleteAllBlogCategories";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import BlogCategoriesTableRow from "./BlogCategoriesTableRow";
import { BlogCategoriesTableColumns } from "./BlogCategoriesTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const BlogCategoriesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("blog-categories.view"),
            create: has("blog-categories.create"),
            edit: has("blog-categories.update"),
            delete: has("blog-categories.delete"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const [trashedFilter, setTrashedFilter] = useState("");

    const { deleteBlogCategoryMutate, isPending: isDelete } = useDeleteBlogCategory();
    const { deleteAllBlogCategoriesMutate, isPending: isDeleteAll } = useDeleteAllBlogCategories();
    const { restoreAllBlogCategoriesMutate, isPending: isRestoreAll } = useRestoreAllBlogCategories();
    const { forceDeleteAllBlogCategoriesMutate, isPending: isForceDeleteAll } = useForceDeleteAllBlogCategories();

    const {
        page, perPage, search, order, selectedIds, menuAnchorEl,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handleEdit, handleMenuDelete, handleMenuRestore,
        handleBulkDelete, handleBulkRestore, handlePageChange, handlePerPageChange,
    } = useTableActions({
        basePath: "/blog/categories",
        deleteSingle: deleteBlogCategoryMutate,
        deleteAll: deleteAllBlogCategoriesMutate,
        restoreSingle: (id, options) => restoreAllBlogCategoriesMutate({ items: [id] }, options),
        restoreAll: restoreAllBlogCategoriesMutate,
        forceDeleteSingle: (id, options) => forceDeleteAllBlogCategoriesMutate({ items: [id] }, options),
        forceDeleteAll: forceDeleteAllBlogCategoriesMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page, per_page: perPage, search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            sort_by: 'created_at', sort_order: order,
        }),
        [page, perPage, search, trashedFilter, order]
    );

    const { isLoading, error, categories, meta } = useGetBlogCategories(params);
    const columns = useMemo(() => BlogCategoriesTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "تصنيفات المدونة", en: "Blog Categories" }}
            columns={columns}
            items={categories}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={[]}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, categories)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/blog/categories/create")}
            addButtonLabel={{ ar: "إضافة تصنيف", en: "Add Category" }}
            page={page} perPage={perPage} order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <BlogCategoriesTableRow
                    {...params} key={params.item.id} item={params.item}
                    perms={perms} onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default BlogCategoriesTable;
